import { contentSectionToMarkdown } from './kaiCoachContent';
import type {
  RawActivity,
  RawCoachGoal,
  RawCoachScoring,
  RawFocusArea,
  RawInstructionBlock,
  RawInstructionFile,
  RawKaiCoachSection,
  RawSession,
} from './kaiCoachTypes';

// Editor-side model for a Kai Coach. Coach text (orientation, prompts, coaching
// notes) is authored as MARKDOWN and stored directly in a ContentSection's
// content unit `text` — that text becomes the AI brief, so a structured/HTML
// editor would pollute it. The student view renders the same markdown.

export type EditableVisibility = 'DRAFT' | 'LIVE' | 'SCHEDULED';

export interface EditableFile {
  description: string;
  downloadUrl: string;
  filename: string;
  storagePath: string;
  uid: string;
}

export interface EditableNote {
  content: string;
  files: EditableFile[];
  label: string;
  liveAtMillis: string;
  uid: string;
  visibility: EditableVisibility;
}

export type EditableScoringMethod = 'PARTICIPATION' | 'GOAL_REACHED';

// A scorable unit's goal under goal-reached credit, as the editor authors it:
// one criterion (private to Kai) and the line students see. The criterion uid is
// kept so a saved goal keeps its identity across edits.
export interface EditableGoal {
  goalCriteria: string;
  goalCriterionUid: string;
  goalSummary: string;
}

// A focus area the student can pick before their next message. title is shown to
// students in a dropdown; instructions are hidden and only sent to Kai for the
// turn the student selects this area.
export interface EditableFocusArea {
  instructions: string;
  title: string;
  uid: string;
}

export interface EditableActivity extends EditableGoal {
  // Always empty in the editor. Chapter context is authored on the SESSION and
  // applies to every activity in it; any activity-level uids stored before that
  // rule are folded into the session's list on load (see parseSession) and the
  // activity is saved with an empty list, so nothing is ever injected that the
  // editor does not show. The proto field stays for future flexibility.
  contextComponentUids: string[];
  // UTC millis as a string, '' when unset. Overrides the session's due date for
  // this activity's thread.
  dueAtMillis: string;
  focusAreas: EditableFocusArea[];
  label: string;
  notes: EditableNote[];
  // Empty string when unset: under goal-reached credit the coach's default
  // applies; "0" is kept as an explicit 0 (no credit for this unit). Authored per
  // activity when a session is split; ignored otherwise.
  points: string;
  prompt: string;
  requiredInteractions: string;
  uid: string;
}

export interface EditableSession extends EditableGoal {
  activities: EditableActivity[];
  // Uids of other course chapters attached as AI background context for this
  // session and all of its activities.
  contextComponentUids: string[];
  // UTC millis as a string, '' when unset: after this, students can no longer
  // send in the session (or its activities, unless one has its own due date).
  dueAtMillis: string;
  focusAreas: EditableFocusArea[];
  label: string;
  notes: EditableNote[];
  openAtMillis: string;
  // Credit on the session (used when NOT split into activities): '' when unset
  // (the coach's default applies under goal-reached credit), "0" for none.
  points: string;
  prompt: string;
  requiredInteractions: string;
  uid: string;
}

export interface EditableCoach {
  // Credit is off by default: a coach has no score unless the instructor
  // enables it.
  creditEnabled: boolean;
  // Goal-reached credit: what every session (or activity, when a session is
  // split) uses for each credit value it leaves blank. Saved whatever the
  // method, like unit goals, so switching methods loses nothing.
  defaultGoal: EditableGoal;
  defaultPoints: string;
  orientation: string;
  provider: string;
  scoringMethod: EditableScoringMethod;
  sessions: EditableSession[];
  standingInstructions: EditableNote[];
  // The module's name. It lives on the component, not the coach section, so it
  // is not part of the serialized section: the save payload writes it onto the
  // component instead.
  title: string;
}

function uid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `id-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function str(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

// A repeated string proto field, tolerating a missing/!array value and dropping
// non-string or blank entries.
function strList(value: unknown): string[] {
  return Array.isArray(value)
    ? value
        .map((entry) => str(entry).trim())
        .filter((entry) => entry !== '')
    : [];
}

// Points are edited as strings too, but an explicit 0 is kept as "0": under
// goal-reached credit a blank unit uses the coach's default while 0 takes the
// unit out of credit, so the two must not collapse into one.
function pointsStr(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return value.trim();
  }
  return '';
}

// Number fields (required_interactions) are edited as strings. A blank, zero or
// non-finite value becomes '' (unset); the serializer omits empty values.
function numStr(value: unknown): string {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value === 0 ? '' : String(value);
  }
  if (typeof value === 'string' && value.trim() && Number.isFinite(Number(value))) {
    return Number(value) === 0 ? '' : value.trim();
  }
  return '';
}

function parseVisibility(value: unknown): EditableVisibility {
  if (value === 1 || value === 'LIVE') {
    return 'LIVE';
  }
  if (value === 2 || value === 'SCHEDULED') {
    return 'SCHEDULED';
  }
  return 'DRAFT';
}

function parseFile(raw: RawInstructionFile): EditableFile {
  return {
    description: str(raw.instructor_description ?? raw.instructorDescription),
    downloadUrl: str(raw.download_url ?? raw.downloadUrl),
    filename: str(raw.filename),
    storagePath: str(raw.storage_path ?? raw.storagePath),
    uid: str(raw.uid) || uid(),
  };
}

// Authored order is preserved positionally (notes keep array order; serialize
// writes the `order` field from the index).
function parseNote(raw: RawInstructionBlock): EditableNote {
  return {
    content: contentSectionToMarkdown(raw.content),
    files: (raw.files ?? []).map(parseFile),
    label: str(raw.label),
    liveAtMillis: str(raw.live_at_utc_milli ?? raw.liveAtUtcMilli),
    uid: str(raw.uid) || uid(),
    visibility: parseVisibility(raw.visibility),
  };
}

function parseFocusArea(raw: RawFocusArea): EditableFocusArea {
  return {
    instructions: str(raw.instructions),
    title: str(raw.title),
    uid: str(raw.uid) || uid(),
  };
}

function parseScoringMethod(scoring: RawCoachScoring | null | undefined): EditableScoringMethod {
  const method = scoring?.method;
  return method === 'GOAL_REACHED' || method === 1 ? 'GOAL_REACHED' : 'PARTICIPATION';
}

// The editor authors one criterion; any further ones (from a later client) are
// folded into its text so nothing is silently dropped from view.
function parseGoal(raw: RawCoachGoal | null | undefined): EditableGoal {
  const criteria = (raw?.criteria ?? []).filter((criterion) => str(criterion?.text).trim() !== '');
  return {
    goalCriteria: criteria.map((criterion) => str(criterion.text).trim()).join('\n\n'),
    goalCriterionUid: str(criteria[0]?.uid) || uid(),
    goalSummary: str(raw?.student_summary ?? raw?.studentSummary),
  };
}

function parseActivity(raw: RawActivity): EditableActivity {
  return {
    ...parseGoal(raw.goal),
    contextComponentUids: strList(
      raw.context_component_uids ?? raw.contextComponentUids,
    ),
    dueAtMillis: str(raw.due_at_utc_milli ?? raw.dueAtUtcMilli),
    focusAreas: (raw.focus_areas ?? raw.focusAreas ?? []).map(parseFocusArea),
    label: str(raw.label),
    notes: (raw.coaching_notes ?? raw.coachingNotes ?? []).map(parseNote),
    points: pointsStr(raw.points),
    prompt: contentSectionToMarkdown(raw.assignment_prompt ?? raw.assignmentPrompt),
    requiredInteractions: numStr(raw.required_interactions ?? raw.requiredInteractions),
    uid: str(raw.uid) || uid(),
  };
}

function parseSession(raw: RawSession): EditableSession {
  const activities = (raw.activities ?? []).map(parseActivity);
  return {
    ...parseGoal(raw.goal),
    // Fold any legacy activity-level chapters up into the session (session
    // first, order preserved, de-duplicated) and leave the activities empty.
    activities: activities.map((activity) => ({ ...activity, contextComponentUids: [] })),
    contextComponentUids: serializeContextUids([
      ...strList(raw.context_component_uids ?? raw.contextComponentUids),
      ...activities.flatMap((activity) => activity.contextComponentUids),
    ]),
    dueAtMillis: str(raw.due_at_utc_milli ?? raw.dueAtUtcMilli),
    focusAreas: (raw.focus_areas ?? raw.focusAreas ?? []).map(parseFocusArea),
    label: str(raw.label),
    notes: (raw.coaching_notes ?? raw.coachingNotes ?? []).map(parseNote),
    openAtMillis: str(raw.open_at_utc_milli ?? raw.openAtUtcMilli),
    points: pointsStr(raw.points),
    prompt: contentSectionToMarkdown(raw.assignment_prompt ?? raw.assignmentPrompt),
    requiredInteractions: numStr(raw.required_interactions ?? raw.requiredInteractions),
    uid: str(raw.uid) || uid(),
  };
}

export interface ToEditableCoachOptions {
  // The component's title, which is edited alongside the section.
  title?: string | null;
}

export function toEditableCoach(
  section: RawKaiCoachSection | null | undefined,
  options: ToEditableCoachOptions = {},
): EditableCoach {
  return {
    creditEnabled: section?.scoring?.enabled === true,
    defaultGoal: parseGoal(section?.scoring?.default_goal ?? section?.scoring?.defaultGoal),
    defaultPoints: pointsStr(section?.scoring?.default_points ?? section?.scoring?.defaultPoints),
    orientation: contentSectionToMarkdown(
      section?.student_orientation ?? section?.studentOrientation,
    ),
    provider: (() => {
      const p = section?.config?.provider;
      return typeof p === 'string' ? p : 'OPENAI';
    })(),
    scoringMethod: parseScoringMethod(section?.scoring),
    sessions: (section?.sessions ?? []).map(parseSession),
    standingInstructions: (
      section?.standing_instructions ??
      section?.standingInstructions ??
      []
    ).map(parseNote),
    title: str(options.title),
  };
}

// --- factories --------------------------------------------------------------

export function newNote(): EditableNote {
  return { content: '', files: [], label: '', liveAtMillis: '', uid: uid(), visibility: 'DRAFT' };
}

export function newFocusArea(): EditableFocusArea {
  return { instructions: '', title: '', uid: uid() };
}

export function newGoal(): EditableGoal {
  return { goalCriteria: '', goalCriterionUid: uid(), goalSummary: '' };
}

export function newSession(): EditableSession {
  return {
    ...newGoal(),
    activities: [],
    contextComponentUids: [],
    dueAtMillis: '',
    focusAreas: [],
    label: '',
    notes: [],
    openAtMillis: '',
    points: '',
    prompt: '',
    requiredInteractions: '',
    uid: uid(),
  };
}

export function newActivity(): EditableActivity {
  return {
    ...newGoal(),
    contextComponentUids: [],
    dueAtMillis: '',
    focusAreas: [],
    label: '',
    notes: [],
    points: '',
    prompt: '',
    requiredInteractions: '',
    uid: uid(),
  };
}

// --- serialization back to the raw proto-shaped section ---------------------

function markdownContent(text: string): { content_units: Array<{ text: string }> } | undefined {
  const trimmed = text.trim();
  if (!trimmed) {
    return undefined;
  }
  return { content_units: [{ text: trimmed }] };
}

function serializeFile(file: EditableFile): Record<string, unknown> {
  return {
    download_url: file.downloadUrl,
    filename: file.filename,
    instructor_description: file.description,
    storage_path: file.storagePath,
    uid: file.uid,
  };
}

// `forceLive` is used for session/activity coaching notes: they have no
// per-note visibility control and are sent to Kai once their session opens, so
// they are always serialized LIVE. Standing instructions keep their authored
// visibility.
function serializeNote(
  note: EditableNote,
  order: number,
  forceLive = false,
): Record<string, unknown> {
  const visibility = forceLive ? 'LIVE' : note.visibility;
  return {
    content: markdownContent(note.content),
    files: note.files.map(serializeFile),
    label: note.label,
    live_at_utc_milli: visibility === 'SCHEDULED' ? note.liveAtMillis : '',
    order,
    uid: note.uid,
    visibility,
  };
}

// Parse an editor number string back to a finite number, or undefined when
// blank. Authored points/required_interactions are retained on the section even
// when credit is toggled off (the backend only scores when scoring.enabled), so
// the instructor doesn't lose them on a toggle.
function numOrUndefined(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : undefined;
}

// The defaults are written only when set, so a coach that never used them —
// every participation coach so far — saves exactly the scoring it always has.
function serializeScoring(coach: EditableCoach): Record<string, unknown> {
  const defaultPoints = numOrUndefined(coach.defaultPoints);
  const defaultGoal = serializeGoal(coach.defaultGoal);
  return {
    ...(defaultGoal ? { default_goal: defaultGoal } : {}),
    ...(defaultPoints !== undefined ? { default_points: defaultPoints } : {}),
    enabled: coach.creditEnabled,
    method: coach.scoringMethod,
  };
}

// A goal is omitted when both of its fields are blank. Kept whatever the method,
// like points, so switching methods back and forth never loses what was written.
function serializeGoal(goal: EditableGoal): Record<string, unknown> | undefined {
  const criteria = goal.goalCriteria.trim();
  const summary = goal.goalSummary.trim();
  if (!criteria && !summary) {
    return undefined;
  }
  return {
    criteria: criteria ? [{ text: criteria, uid: goal.goalCriterionUid }] : [],
    student_summary: summary,
  };
}

// Blank and duplicate chapter uids are dropped: they would resolve to nothing
// server-side and inject the same chapter twice.
function serializeContextUids(uids: string[]): string[] {
  const seen = new Set<string>();
  return uids
    .map((value) => value.trim())
    .filter((value) => {
      if (value === '' || seen.has(value)) {
        return false;
      }
      seen.add(value);
      return true;
    });
}

// A focus area with no title is dropped: it would be an unlabeled dropdown
// option a student could not distinguish.
function serializeFocusAreas(areas: EditableFocusArea[]): Array<Record<string, unknown>> {
  return areas
    .filter((area) => area.title.trim() !== '')
    .map((area) => ({
      instructions: area.instructions.trim(),
      title: area.title.trim(),
      uid: area.uid,
    }));
}

function serializeActivity(activity: EditableActivity): Record<string, unknown> {
  return {
    assignment_prompt: markdownContent(activity.prompt),
    // Never authored per activity any more; the session's list applies.
    context_component_uids: [],
    coaching_notes: activity.notes.map((note, index) => serializeNote(note, index, true)),
    due_at_utc_milli: activity.dueAtMillis,
    focus_areas: serializeFocusAreas(activity.focusAreas),
    goal: serializeGoal(activity),
    label: activity.label,
    points: numOrUndefined(activity.points),
    required_interactions: numOrUndefined(activity.requiredInteractions),
    uid: activity.uid,
  };
}

function serializeSession(session: EditableSession): Record<string, unknown> {
  return {
    activities: session.activities.map(serializeActivity),
    assignment_prompt: markdownContent(session.prompt),
    context_component_uids: serializeContextUids(session.contextComponentUids),
    coaching_notes: session.notes.map((note, index) => serializeNote(note, index, true)),
    due_at_utc_milli: session.dueAtMillis,
    focus_areas: serializeFocusAreas(session.focusAreas),
    goal: serializeGoal(session),
    label: session.label,
    open_at_utc_milli: session.openAtMillis,
    points: numOrUndefined(session.points),
    required_interactions: numOrUndefined(session.requiredInteractions),
    uid: session.uid,
  };
}

// The session/activity dimension names are fixed ("Session"/"Activity") and the
// model is server-chosen, so neither is authored in the editor.
export function serializeCoachSection(
  coach: EditableCoach,
  existingUid: string,
): Record<string, unknown> {
  return {
    activity_dimension_label: 'Activity',
    config: { provider: coach.provider },
    scoring: serializeScoring(coach),
    session_dimension_label: 'Session',
    sessions: coach.sessions.map(serializeSession),
    standing_instructions: coach.standingInstructions.map((note, index) =>
      serializeNote(note, index),
    ),
    student_orientation: markdownContent(coach.orientation),
    uid: existingUid || uid(),
  };
}
